import { Theme } from '@mui/material';

import { isValidTimelineColor } from '@/shared/lib/clientTimelineColor';

import { TimelineBooking } from './timeline.types';

const OWN_FALLBACK = '#8E6DE1';

/**
 * Redecoration's family colour — shared by the committed segment's solid fill and the staged
 * indicator's line, so a redecoration reads as one thing whether it is committed or still being
 * drafted.
 *
 * `700`, not `500`: every tenant's `accent.main` is some palette's `500` shade (location =
 * purple, advertiser = magenta, service = blue itself, backoffice = orange), so a redecoration
 * drawn at `500` is indistinguishable from an ordinary own-booked bar under at least one tenant
 * theme. See timelineColors.test.ts for the per-tenant check.
 */
export const getRedecorationColor = (theme: Theme): string => theme.palette.blue[700];

// Resolves the fill color of a timeline bar from its booking status (falling back to
// own/competitor coloring when no status is set). Shared by the grid cell renderer
// and the list-view strip so both stay visually in sync.
export const getBookingBarColor = (booking: TimelineBooking, theme: Theme): string => {
  // A committed redecoration is its own segment regardless of status, so it needs a fill no
  // other segment uses — otherwise it reads as one more stretch of the same booking.
  if (booking.isRedecoration) return getRedecorationColor(theme);

  const clientColor = isValidTimelineColor(booking.clientColor) ? booking.clientColor : undefined;

  // Yours, but another order's: drawn in the redecoration family instead of the accent, so the
  // accent stays exclusive to the order this rail is about (its base bar and extension segments).
  if (booking.isOwnOtherOrder) return clientColor ?? theme.palette.blue[700];

  // A row the table will not let you book must never read as free, however unsold the stretch is.
  // Read off its own field rather than off `status`: occupancy arrives as BOOKED, so keying this
  // off the status would repaint every occupied bar in the app and lose whose booking it is.
  // Outside the row's own calendar is grey — not on offer — while the stamped statuses stay
  // orange: a warning about the period, next to the orange bars of whoever holds it.
  if (booking.unavailableStatus === 'OUTSIDE_AVAILABILITY') return theme.palette.grey[300];
  if (booking.unavailableStatus) return theme.palette.orange[500];

  const ownColor = theme.palette.accent?.main || OWN_FALLBACK;

  switch (booking.status) {
    case 'AVAILABLE':
      return theme.palette.green[500];
    case 'CONFLICT':
      return theme.palette.red[500];
    case 'UNAVAILABLE':
      return theme.palette.grey[300];
    // Booked or merely reserved, the bar is coloured by WHOSE it is. A pending booking of ours is
    // still ours; drawing it orange made it look like a competitor was holding the space. A client
    // with a configured color is the most specific "whose" there is, so it beats both fills.
    default:
      return clientColor ?? (booking.isOwn ? ownColor : theme.palette.orange[400]);
  }
};

export interface TimelineClientInfo {
  timelineColor?: string | null;
  companyName?: string | null;
}

/**
 * The frontend half of the visibility gate: client colors and names are a location-only
 * feature, so rails that stamp bars from data every tenant receives (an order's embedded
 * client, a details row's companyName) must pass their client through this. Segment rails
 * skip it — the backend already withholds the fields from other viewers there.
 */
export const clientInfoForViewer = (
  userType: 'backoffice' | 'location' | 'advertiser' | 'service' | undefined,
  client: TimelineClientInfo | undefined,
): TimelineClientInfo | undefined => (userType === 'location' ? client : undefined);

// A bar that reports a state or draws as an indicator rather than someone's booking period. Never
// stamped, so a conflict or a free gap cannot end up tooltipping a client it does not belong to.
const isStateBar = (bar: TimelineBooking): boolean =>
  !!bar.unavailableStatus ||
  !!bar.isExtension ||
  !!bar.isStagedRedecoration ||
  bar.status === 'AVAILABLE' ||
  bar.status === 'CONFLICT' ||
  bar.status === 'UNAVAILABLE';

/**
 * Stamps one client's color and name onto the bars of a rail that already knows its client
 * (order page, media plan, details rows). Invalid colors and empty names are dropped here so
 * call sites stay one expression; `getBookingBarColor` still guards `clientColor` on
 * consumption, covering bars stamped by the segment mapper instead of this helper.
 */
export const withClientInfo = (
  bars: TimelineBooking[],
  client?: TimelineClientInfo,
): TimelineBooking[] => {
  const rawColor = client?.timelineColor;
  const clientColor = isValidTimelineColor(rawColor) ? rawColor : undefined;
  const clientName = client?.companyName || undefined;

  if (!clientColor && !clientName) return bars;

  return bars.map((bar) =>
    isStateBar(bar)
      ? bar
      : {
          ...bar,
          ...(clientColor ? { clientColor } : {}),
          ...(clientName ? { clientName } : {}),
        },
  );
};
